import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from "@angular/router";
import { isAdmin } from "@app/_helpers/permission";
import { fromRxValue } from "@app/_helpers/utils";
import { environment } from "@env/environment";
import { IsBetaWindow } from "@env/version";
import { UserSettings, UserSettingsQuery } from "@timeghost/web-api";
import { filter } from "rxjs";

@Injectable({
	providedIn: "root",
})
export class CanAccessBetaGuard {
	private userSettings = fromRxValue<UserSettings>(() =>
		this.userSettingsQuery.select().pipe(filter((x) => !!x?.workspace))
	);
	constructor(
		private userSettingsQuery: UserSettingsQuery,
		private router: Router
	) {}

	async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
		// if dev env, allow access - dev build is always beta
		if (!environment.production) return true;

		if (!IsBetaWindow()) {
			// If not in beta environment, allow access
			return true;
		}
		if (!this.userSettings.value?.workspace) return true;
		// Get user settings
		const userSettings = this.userSettings.value;

		// Admins always have access to beta features
		if (isAdmin(userSettings)) {
			return true;
		}

		// For non-admins, check the workspace setting
		const nonAdminsCanAccessBeta = userSettings.workspace.settings?.nonAdminsCanAccessBeta;

		// If the setting is explicitly set to false, non-admins cannot access
		if (nonAdminsCanAccessBeta === false) {
			return this.redirectToProduction(state.url);
		}

		// If the setting is true, undefined, or null, non-admins can access
		return true;
	}

	private redirectToProduction(betaUrl: string): UrlTree {
		// Convert beta URL to production URL
		let productionUrl = betaUrl;

		// If we're on web-beta.timeghost.io, redirect to web.timeghost.io
		if (window.location.hostname === "web-beta.timeghost.io") {
			const productionHostname = "web.timeghost.io";
			const url = new URL(window.location.href);
			url.hostname = productionHostname;
			productionUrl = url.pathname + url.search + url.hash;
		}

		// Create URL tree for the production route
		return this.router.createUrlTree([productionUrl]);
	}
}
